import React, { useState, useEffect, useRef } from "react";
import { X, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, Info } from "lucide-react";
import * as XLSX from "xlsx";
import guestService from "../../services/guestService";

export default function ModalCargarExcel({
  isOpen,
  onClose,
  eventoId,
  mesasCanvas,
  onAplicarAsignaciones
}) {
  const [file, setFile] = useState(null);
  const [invitadosDB, setInvitadosDB] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filasValidadas, setFilasValidadas] = useState([]);
  const [resumen, setResumen] = useState({ total: 0, validas: 0, invalidas: 0 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && eventoId) {
      cargarInvitados();
      setFile(null);
      setFilasValidadas([]);
      setResumen({ total: 0, validas: 0, invalidas: 0 });
    }
  }, [isOpen, eventoId]);

  const cargarInvitados = async () => {
    setCargando(true);
    try {
      // Obtenemos todos los invitados del evento para validación
      const res = await guestService.getGuests(eventoId);
      if (res.success) {
        setInvitadosDB(res.guests || []);
      } else {
        console.error("Error al cargar invitados para validación", res.error);
      }
    } catch (error) {
      console.error("Error de red al cargar invitados", error);
    } finally {
      setCargando(false);
    }
  };

  const procesarExcel = async (e) => {
    const fileObj = e.target.files[0];
    if (!fileObj) return;

    setFile(fileObj);
    setProcesando(true);

    try {
      const data = await fileObj.arrayBuffer();
      // raw: true previene que la librería intente convertir "12,12,11,1" en un número gigante al leer CSV
      const workbook = XLSX.read(data, { type: "array", raw: true });
      
      let targetSheetName = null;
      let headerRowIndex = 0;

      // Buscar hoja y fila de encabezados dinámicamente
      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
        
        const foundIdx = rawRows.findIndex(row => {
          if (!row || !Array.isArray(row)) return false;
          const hasAlumno = row.some(cell => typeof cell === 'string' && cell.trim().toUpperCase() === "ALUMNO");
          const hasCorreo = row.some(cell => typeof cell === 'string' && cell.trim().toUpperCase() === "CORREO");
          const hasBoletos = row.some(cell => typeof cell === 'string' && cell.trim().toUpperCase() === "BOLETOS");
          
          return hasAlumno && hasCorreo && hasBoletos;
        });

        if (foundIdx !== -1) {
          targetSheetName = sheetName;
          headerRowIndex = foundIdx;
          break;
        }
      }

      if (!targetSheetName) {
        throw new Error("No se encontró ninguna hoja con las columnas requeridas (ALUMNO o CORREO, y BOLETOS).");
      }

      // raw: false asegura que obtengamos el valor formateado (como texto) en lugar de la fecha o número en bruto
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[targetSheetName], { 
        range: headerRowIndex,
        defval: "", 
        raw: false 
      });

      validarFilas(rows);
    } catch (error) {
      console.error("Error al leer el Excel", error);
      alert("No se pudo leer el archivo Excel. Asegúrate de que tenga el formato correcto.");
    } finally {
      setProcesando(false);
      // Limpiar input para permitir subir el mismo archivo de nuevo si hubo error
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const limpiarTexto = (str) => {
    if (!str) return "";
    return String(str)
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " "); // Collapse multiple spaces
  };

  const parsearListaNumeros = (valor) => {
    if (valor === undefined || valor === null || valor === "") return [];
    return String(valor).split(",").map(s => {
      const num = parseInt(s.trim(), 10);
      return isNaN(num) ? null : num;
    }).filter(n => n !== null);
  };

  const validarFilas = (rows) => {
    const validadas = [];
    let countValidas = 0;
    let countInvalidas = 0;

    // Clonamos las mesas para poder simular la ocupación progresiva
    const mesasSimuladas = mesasCanvas.map(m => ({ ...m, invitadosAsignadosSimulados: m.invitados || 0 }));

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const numFila = index + 2; // +1 por ser 0-indexed, +1 por la cabecera (aproximado debido al offset dinámico)
      const errores = [];
      const warnings = [];

      // Helper para leer columnas ignorando espacios extra en los encabezados
      const getCol = (nombreColumna) => {
        const key = Object.keys(row).find(k => k.trim().toUpperCase() === nombreColumna);
        return key ? row[key] : "";
      };

      // 1. Extraer columnas y parsear valores
      const nombreAlumno = String(getCol("ALUMNO")).trim();
      const correoAlumno = String(getCol("CORREO")).trim();
      
      // Loggear la fila cruda solo para el primer alumno para ver qué columnas hay
      if (index === 0) {
        console.log("Fila Excel detectada:", row);
        console.log("Columnas encontradas:", Object.keys(row));
      }

      const boletosTotal = parseInt(getCol("BOLETOS"), 10) || 0;
      
      const numerosMesa = parsearListaNumeros(getCol("NUMERO DE MESA"));
      const boletosPorMesa = parsearListaNumeros(getCol("BOLETOS POR MESA"));
      
      const menuNormal = parseInt(getCol("MENU NORMAL"), 10) || 0;
      const menuInfantil = parseInt(getCol("MENU INFANTIL"), 10) || 0;
      const menuEspecial = parseInt(getCol("MENU ESPECIAL"), 10) || 0;
      const comentarios = String(getCol("COMENTARIOS (ALERGIAS)")).trim();

      // Detectar fin de tabla o fila de totales
      if (!nombreAlumno && !correoAlumno) {
        // Si no hay nombre ni correo, pero hay muchos boletos, asumimos que son totales
        if (boletosTotal > 100 && !row["NUMERO DE MESA"]) {
          break; // Terminamos de procesar
        }
        // Ignorar filas completamente vacías
        if (!row["BOLETOS"] && !row["NUMERO DE MESA"]) continue; 
        
        errores.push("El campo ALUMNO o CORREO están vacíos.");
      }

      // 2. Validar invitado en BD
      let invitadoId = null;
      let invitadoObj = null;
      if (nombreAlumno) {
        const nombreLimpio = limpiarTexto(nombreAlumno);
        
        // 1. Bypass para invitados especiales / genéricos
        const keywordsBypass = ["maestro", "maestra", "comite", "matiz", "mesa"];
        const esInvitadoEspecial = keywordsBypass.some(kw => nombreLimpio.includes(kw));

        if (esInvitadoEspecial) {
          invitadoId = `gen-${nombreLimpio.replace(/\s+/g, '-')}`;
          invitadoObj = {
            id: invitadoId,
            nombre_completo: nombreAlumno,
            cantidad_boletos: boletosTotal,
            es_generico: true
          };
          warnings.push(`Asignado como invitado genérico/especial: "${nombreAlumno}".`);
        } else {
          // Búsqueda normal en base de datos usando CORREO
          const match = invitadosDB.find(inv => {
            const correoDB = limpiarTexto(inv.correo || inv.email || "");
            const correoExcel = limpiarTexto(correoAlumno);

            if (correoExcel && correoDB === correoExcel) return true;

            /*
            // Búsqueda por nombre comentada a petición
            // A. Intentar buscar por nombre_completo primero
            const nombreCompletoDB = limpiarTexto(inv.nombre_completo || "");
            if (nombreCompletoDB === nombreLimpio) return true;

            // B. Intentar buscar por campos separados
            const partesNombre = [
              inv.nombre || inv.nombre_graduado || "",
              inv.apellido_paterno || "",
              inv.apellido_materno || ""
            ];
            const fullNameDB = partesNombre.filter(p => p.trim() !== "").join(" ");
            const nameDB = limpiarTexto(fullNameDB);
            
            return nameDB === nombreLimpio;
            */
            
            return false;
          });

          if (match) {
            invitadoId = match.id;
            invitadoObj = match;
            
            // Validar cantidad (ahora solo muestra warning, no error)
            const boletosBD = parseInt(match.boletos || match.cantidad_boletos || 0, 10);
            if (boletosBD > 0 && boletosTotal !== boletosBD) {
              warnings.push(`Diferencia de boletos: En BD tiene ${boletosBD}, pero el Excel asigna ${boletosTotal}. Se tomará la del Excel.`);
            }
          } else {
            errores.push(`Invitado "${nombreAlumno}" no encontrado en el evento.`);
          }
        }
      }

      if (boletosTotal <= 0) {
        errores.push("Cantidad de BOLETOS inválida.");
      }

      if (numerosMesa.length === 0) {
        errores.push("NUMERO DE MESA inválido o vacío.");
      }

      // Si BOLETOS POR MESA está vacío pero solo hay 1 mesa, se asigna el total a esa mesa
      let distribucionBoletos = boletosPorMesa;
      if (numerosMesa.length === 1 && distribucionBoletos.length === 0) {
        distribucionBoletos = [boletosTotal];
      }

      if (numerosMesa.length !== distribucionBoletos.length) {
        errores.push(`La cantidad de mesas (${numerosMesa.length}) no coincide con la cantidad de separaciones en BOLETOS POR MESA (${distribucionBoletos.length}).`);
      }

      const sumaBoletosMesas = distribucionBoletos.reduce((a, b) => a + b, 0);
      if (distribucionBoletos.length > 0 && sumaBoletosMesas !== boletosTotal) {
        errores.push(`La suma de BOLETOS POR MESA (${sumaBoletosMesas}) no coincide con el total de BOLETOS (${boletosTotal}).`);
      }

      // 3. Validar mesas en el Canvas
      const asignacionesMesas = [];
      if (errores.length === 0) {
        for (let i = 0; i < numerosMesa.length; i++) {
          const numMesa = numerosMesa[i];
          const cantAsignar = distribucionBoletos[i];
          
          const mesaCanvas = mesasSimuladas.find(m => m.numero === numMesa && (m.type === "mesa" || m.type === "mesaRectangular"));
          if (!mesaCanvas) {
            errores.push(`La mesa ${numMesa} no existe en el croquis.`);
            continue;
          }

          const capacidad = parseInt(mesaCanvas.capacidad || 10, 10);
          const disponibles = capacidad - mesaCanvas.invitadosAsignadosSimulados;

          if (cantAsignar > disponibles) {
            warnings.push(`⚠️ Sobrecupo en mesa ${numMesa} (Capacidad: ${capacidad}, Disponibles: ${disponibles}, Se asignan: ${cantAsignar})`);
          }
          
          // Actualizar simulador
          mesaCanvas.invitadosAsignadosSimulados += cantAsignar;
          asignacionesMesas.push({
            mesa_id: mesaCanvas.id,
            numero_mesa: numMesa,
            cantidad: cantAsignar
          });
        }
      }

      // 4. Lógica Flexible de Menús
      let poolMenus = [];
      const totalMenusDeclarados = menuNormal + menuInfantil + menuEspecial;
      
      for (let i = 0; i < menuNormal; i++) poolMenus.push({ tipoMenu: "normal" });
      for (let i = 0; i < menuInfantil; i++) poolMenus.push({ tipoMenu: "infantil" });
      for (let i = 0; i < menuEspecial; i++) poolMenus.push({ tipoMenu: "especial" });

      // Si faltan, rellenar con normal
      if (poolMenus.length < boletosTotal) {
        const faltantes = boletosTotal - poolMenus.length;
        for (let i = 0; i < faltantes; i++) {
          poolMenus.push({ tipoMenu: "normal" });
        }
      }

      // Si sobran, se recortan
      if (poolMenus.length > boletosTotal) {
        poolMenus = poolMenus.slice(0, boletosTotal);
      }

      // Distribuir el pool a las mesas correspondientes
      let offsetMenus = 0;
      asignacionesMesas.forEach(asignacion => {
        const porcionMenus = poolMenus.slice(offsetMenus, offsetMenus + asignacion.cantidad);
        
        asignacion.personasDetalladas = porcionMenus.map((menuObj, i) => ({
          nombre: i === 0 ? nombreAlumno : `${nombreAlumno} - Acompañante ${i}`,
          tipoMenu: menuObj.tipoMenu,
          restricciones: {},
          otraRestriccion: "", // No se guardan las alergias por instrucción
          necesidadesEspeciales: { requiereAccesibilidad: false, comentarios: "" }
        }));
        
        offsetMenus += asignacion.cantidad;
      });

      const esValida = errores.length === 0;
      if (esValida) countValidas++; else countInvalidas++;

      validadas.push({
        fila: numFila,
        nombre: nombreAlumno,
        correo: correoAlumno,
        boletos: boletosTotal,
        invitadoObj,
        asignacionesMesas,
        errores,
        warnings,
        esValida
      });
    }

    setResumen({ total: validadas.length, validas: countValidas, invalidas: countInvalidas });
    setFilasValidadas(validadas);
  };

  const handleAplicar = () => {
    const asignacionesAplicar = filasValidadas.filter(f => f.esValida);
    onAplicarAsignaciones(asignacionesAplicar);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Cargar Selección de Mesas</h2>
              <p className="text-sm text-gray-500">Sube un Excel para asignar mesas por lote</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {cargando ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">Obteniendo lista de invitados de la BD...</p>
            </div>
          ) : (
            <>
              {/* Zona de subida */}
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center mb-6 transition-colors
                  ${filasValidadas.length > 0 ? 'border-green-200 bg-green-50' : 'border-gray-300 hover:border-primary-400 bg-gray-50'}
                `}
              >
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={procesarExcel}
                  className="hidden"
                  ref={fileInputRef}
                  id="excel-upload"
                />
                <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center">
                  <div className={`p-4 rounded-full mb-3 ${filasValidadas.length > 0 ? 'bg-green-100 text-green-600' : 'bg-white text-primary-500 shadow-sm'}`}>
                    {filasValidadas.length > 0 ? <CheckCircle className="w-8 h-8" /> : <Upload className="w-8 h-8" />}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">
                    {filasValidadas.length > 0 ? 'Archivo cargado' : 'Seleccionar archivo Excel'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {filasValidadas.length > 0 ? 'Haz clic para seleccionar un archivo diferente' : 'Soporta .xlsx, .xls, .csv'}
                  </p>
                </label>
              </div>

              {procesando && (
                <p className="text-center text-primary-600 my-4 animate-pulse">Procesando filas...</p>
              )}

              {/* Previsualización */}
              {filasValidadas.length > 0 && !procesando && (
                <div className="mt-6">
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 bg-white border border-gray-200 p-4 rounded-lg flex items-center justify-between">
                      <span className="text-gray-500 font-medium">Total Filas</span>
                      <span className="text-2xl font-bold text-gray-800">{resumen.total}</span>
                    </div>
                    <div className="flex-1 bg-green-50 border border-green-200 p-4 rounded-lg flex items-center justify-between">
                      <span className="text-green-700 font-medium">Válidas</span>
                      <span className="text-2xl font-bold text-green-600">{resumen.validas}</span>
                    </div>
                    <div className="flex-1 bg-red-50 border border-red-200 p-4 rounded-lg flex items-center justify-between">
                      <span className="text-red-700 font-medium">Con Errores</span>
                      <span className="text-2xl font-bold text-red-600">{resumen.invalidas}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 text-blue-700 p-3 rounded-lg flex items-start gap-2 text-sm mb-4">
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p>Revise los resultados de la validación. Solo las filas marcadas en verde se aplicarán al croquis. Una vez aplicadas, podrá verificar visualmente y hacer clic en <strong>"Guardar Asignaciones"</strong> para enviarlas a la base de datos.</p>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 w-16 text-center">Fila</th>
                            <th className="px-4 py-3 w-10 text-center">Estado</th>
                            <th className="px-4 py-3">Alumno</th>
                            <th className="px-4 py-3 text-center">Boletos</th>
                            <th className="px-4 py-3">Mesas a Asignar</th>
                            <th className="px-4 py-3 w-1/3">Detalles / Errores</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filasValidadas.map((fila, idx) => (
                            <tr key={idx} className={fila.esValida ? 'bg-white hover:bg-gray-50' : 'bg-red-50/50 hover:bg-red-50'}>
                              <td className="px-4 py-3 text-center font-medium text-gray-500">{fila.fila}</td>
                              <td className="px-4 py-3 text-center">
                                {!fila.esValida ? (
                                  <AlertTriangle className="w-5 h-5 text-red-500 mx-auto" />
                                ) : fila.warnings && fila.warnings.length > 0 ? (
                                  <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto" title="Sobrecupo" />
                                ) : (
                                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-800">{fila.nombre}</td>
                              <td className="px-4 py-3 text-center">{fila.boletos}</td>
                              <td className="px-4 py-3">
                                {fila.esValida ? (
                                  <div className="flex flex-wrap gap-1">
                                    {fila.asignacionesMesas.map((m, i) => (
                                      <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-md border border-gray-200 font-medium">
                                        Mesa {m.numero_mesa} ({m.cantidad})
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">No procesado</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs space-y-2">
                                {!fila.esValida && (
                                  <ul className="list-disc pl-4 text-red-600 space-y-1">
                                    {fila.errores.map((err, i) => <li key={`err-${i}`}>{err}</li>)}
                                  </ul>
                                )}
                                {fila.warnings && fila.warnings.length > 0 && (
                                  <ul className="list-disc pl-4 text-amber-600 font-medium space-y-1">
                                    {fila.warnings.map((warn, i) => <li key={`warn-${i}`}>{warn}</li>)}
                                  </ul>
                                )}
                                {fila.esValida && (!fila.warnings || fila.warnings.length === 0) && (
                                  <span className="text-green-600">Todo correcto</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button 
            onClick={handleAplicar}
            disabled={resumen.validas === 0}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2
              ${resumen.validas > 0 ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-sm' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
            `}
          >
            <CheckCircle className="w-4 h-4" />
            Aplicar {resumen.validas} Asignaciones
          </button>
        </div>
      </div>
    </div>
  );
}
